import { MapsetKmlFormat } from "mobility-toolbox-js/ol";
import { LineString, Polygon } from "ol/geom";

import {
  LINE_STROKE_OPACITY,
  POLYGON_FILL_OPACITY,
  POLYGON_STROKE_OPACITY,
  TEXT_BACKGROUND_OPACITY,
  TEXT_STROKE_OPACITY,
} from "./constants";
import createStyles from "./createStyles";
import getOlColor from "./getOlColor";
import { sortFeaturesByZIndexAndGeom } from "./sortFeaturesByZIndexAndGeom";
import { getFeatureStyle, isPicture } from "./styleUtils";

const KML = new MapsetKmlFormat();

/**
 * This function reads a kml and returns a list of features with their style properly set.
 */
const readKml = (kmlString, map, font) => {
  const features = KML.readFeatures(kmlString, {
    applyMinMaxZoom: false,
    featureProjection: map.getView().getProjection(),
  })
    // For backward compatibility, we sort features by zIndex and geometry type,
    // because old KMLS have very big zIndex values then make the staking order a bit complicated.
    .sort(sortFeaturesByZIndexAndGeom)
    .map((feature, index) => {
      // We reinitialized the zIndex to avoid having very big values
      // that can cause problems with the stacking order.
      feature.set("zIndex", index);

      const style = getFeatureStyle(feature, map.getView().getResolution());

      feature.set("minZoom", feature.get("minZoom") || undefined);
      feature.set("maxZoom", feature.get("maxZoom") || undefined);

      const textStyle = style.getText();
      if (textStyle) {
        feature.set(TEXT_STROKE_OPACITY, textStyle.getFill().getColor()[3]);
        feature.set(
          TEXT_BACKGROUND_OPACITY,
          getOlColor(textStyle.getBackgroundFill()?.getColor())?.[3],
        );

        /* Set label font (e.g. font from cd) */
        if (font) {
          textStyle.setFont(
            /bold /i.test(style.getText().getFont()) ? `bold ${font}` : font,
          );
        }
      }

      const styles = feature.getStyleFunction()(
        feature,
        map.getView().getResolution(),
      );
      const stylesArray = Array.isArray(styles) ? styles : [styles];

      if (isPicture(style)) {
        style.getImage().setRotateWithView(true);
      }

      if (feature.getGeometry() instanceof LineString) {
        // Store the original opacity in the line feature (used for zoomLimits opacity handling)
        const strokeColor =
          style.getStroke().getColor().color || style.getStroke().getColor();
        feature.set(LINE_STROKE_OPACITY, strokeColor[3]);
      }

      if (feature.getGeometry() instanceof Polygon) {
        // Store the original opacity in the Polygon feature (used for zoomLimits opacity handling)
        const strokeColor =
          style.getStroke().getColor().color || style.getStroke().getColor();
        feature.set(POLYGON_STROKE_OPACITY, strokeColor[3]);
        const fill = style.getFill();
        const fillColor = fill
          ? fill.getColor().color || fill.getColor()
          : [0, 0, 0, 0];
        feature.set(POLYGON_FILL_OPACITY, fillColor[3] ?? 0);
      }

      feature.setStyle((feat, res) => {
        return createStyles(feature, res, stylesArray, map);
      });

      return feature;
    });
  return features;
};

export default readKml;
